/**
 * useViewportTier — reactive viewport tier for the website agent's sidebar
 * (FLO-519). Same shape as {@link ./useIsMobile}: a synchronous one-shot read
 * usable during render, plus a hook that stays live across resize/rotate.
 *
 * The tier boundaries live in `components/websiteAgentLayout.ts` (the pure truth
 * table that the unit tests pin); this file only turns them into media queries
 * and subscribes. Reach for it when the breakpoint drives LOGIC — here it gates
 * whether we squeeze the host page at all, which is a JS decision (CSS `@media`
 * cannot toggle `applySqueeze`).
 *
 * Media queries — not `window.innerWidth` — because `innerWidth` includes the
 * classic scrollbar while `@media (min-width: …)` does not; deriving the tier
 * from the same queries we subscribe to keeps the value and the change events
 * from disagreeing at the boundary.
 */

import { useEffect, useState } from 'react';
// FLO-523: read through the PRISTINE matchMedia, never `window.matchMedia`. The
// sidebar-compat viewport facade patches that global to report viewport-minus-
// dock for the host page; if our own tier read saw the lie, docking would push
// the tier below 1024, which releases the squeeze, which un-lies, which
// re-docks — the rail would flap the page forever. Identical behaviour with the
// facade off (see ../sdk/native-viewport).
import { hasMatchMedia, realMatchMedia } from '../sdk/native-viewport';
import {
  CHIP_ROW_MIN_WIDTH_PX,
  MAX_CHIP_ROW,
  MAX_CHIP_ROW_MOBILE,
  RAIL_MIN_WIDTH_PX,
  SQUEEZE_MIN_WIDTH_PX,
  type ViewportTier,
} from '../components/websiteAgentLayout';

/** Matches at/above the squeeze tier ({@link SQUEEZE_MIN_WIDTH_PX}). */
export const SQUEEZE_MEDIA_QUERY = `(min-width: ${SQUEEZE_MIN_WIDTH_PX}px)`;
/** Matches at/above the rail tier ({@link RAIL_MIN_WIDTH_PX}). */
export const RAIL_MEDIA_QUERY = `(min-width: ${RAIL_MIN_WIDTH_PX}px)`;
/** Matches at/above the narrowest width that can carry a chip row (FLO-520). */
export const CHIP_ROW_MEDIA_QUERY = `(min-width: ${CHIP_ROW_MIN_WIDTH_PX}px)`;

/**
 * Synchronous, one-shot read of the current tier. Safe during render and with
 * no `window`/`matchMedia` (SSR or the node test env), where it returns
 * `'squeeze'` — the desktop default, matching `matchesMobile()`'s
 * "assume desktop" degradation.
 */
export function matchesViewportTier(): ViewportTier {
  if (!hasMatchMedia()) return 'squeeze';
  if (realMatchMedia(SQUEEZE_MEDIA_QUERY)?.matches) return 'squeeze';
  if (realMatchMedia(RAIL_MEDIA_QUERY)?.matches) return 'overlay';
  return 'fullscreen';
}

/**
 * Synchronous, one-shot read of how many nudge chips the bottom-center row may
 * show (FLO-520). Media-query-derived for the same reason as the tier, and it
 * degrades to the desktop cap with no `matchMedia` so a non-DOM env never
 * silently hides the row. The pure truth table is `chipRowCapacity(width)`.
 */
export function matchesChipRowCapacity(): number {
  if (!hasMatchMedia()) return MAX_CHIP_ROW;
  if (realMatchMedia(SQUEEZE_MEDIA_QUERY)?.matches) return MAX_CHIP_ROW;
  if (realMatchMedia(CHIP_ROW_MEDIA_QUERY)?.matches) return MAX_CHIP_ROW_MOBILE;
  return 0;
}

/** Returns the live chip-row cap, updating as the viewport crosses a boundary. */
export function useChipRowCapacity(): number {
  const [capacity, setCapacity] = useState<number>(matchesChipRowCapacity);

  useEffect(() => {
    if (!hasMatchMedia()) return;
    const queries = [SQUEEZE_MEDIA_QUERY, CHIP_ROW_MEDIA_QUERY]
      .map((q) => realMatchMedia(q))
      .filter((mql): mql is MediaQueryList => !!mql);
    const onChange = () => setCapacity(matchesChipRowCapacity());
    onChange();
    const teardowns = queries.map((mql) => {
      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
      }
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    });
    return () => teardowns.forEach((off) => off());
  }, []);

  return capacity;
}

/** Returns the live {@link ViewportTier}, updating as the viewport crosses a tier. */
export function useViewportTier(): ViewportTier {
  const [tier, setTier] = useState<ViewportTier>(matchesViewportTier);

  useEffect(() => {
    if (!hasMatchMedia()) return;
    const queries = [SQUEEZE_MEDIA_QUERY, RAIL_MEDIA_QUERY]
      .map((q) => realMatchMedia(q))
      .filter((mql): mql is MediaQueryList => !!mql);
    const onChange = () => setTier(matchesViewportTier());
    // Re-sync once on mount: the viewport may have changed between the initial
    // render and this effect running.
    onChange();
    // `addEventListener` is the modern API; `addListener` is the deprecated
    // fallback for older Safari (< 14) where the former is missing.
    const teardowns = queries.map((mql) => {
      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
      }
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    });
    return () => teardowns.forEach((off) => off());
  }, []);

  return tier;
}
